import React, { useState, useEffect } from 'react';
import { useGetAssignHistoryQuery, useGetHistoryByTimeMutation, useGetErrorCsvByTimeMutation, useGetAllCsvByTimeMutation } from '../../../services/importApi';
import { Layout } from '../../components/Layout';
import { ToolBar } from '../../components/ToolBar';
import { Table, TableBody, TableCell, TableHead, TableRow, Paper, Typography, Button, Modal, Box } from '@mui/material';
import { IImportHistoryByTime } from '../../../services/importApi';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1000,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

// Define the interface for the row data
interface RowData {
    id: number;
    num: number;
    failed: number;
    time: string;
    name: string;
}

export function ImportHistoryPage(props) {
    // Option 1: Use refetchOnMountOrArgChange
    const { data = [], refetch, isLoading } = useGetAssignHistoryQuery(undefined, {
        refetchOnMountOrArgChange: true, // Always refetch when component mounts
        refetchOnFocus: true, // Refetch when window gets focus
        refetchOnReconnect: true, // Refetch when reconnecting
    });

    const [getHistoryByTime] = useGetHistoryByTimeMutation();
    const [getErrorCsvByTime] = useGetErrorCsvByTimeMutation();
    const [getAllCsvByTime] = useGetAllCsvByTimeMutation();
    const [open, setOpen] = useState(false);
    const [details, setDetails] = useState<IImportHistoryByTime[]>([]);
    const [selectedRow, setSelectedRow] = useState<RowData | null>(null);

    // Option 2: Manual refetch on component mount
    useEffect(() => {
        refetch();
    }, [refetch]);

    // Option 3: Refetch when page becomes visible
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                refetch();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refetch]);

    const handleErrorNumClick = async (row: RowData) => {
        try {
            const result = await getErrorCsvByTime(row.time).unwrap();
            const url = window.URL.createObjectURL(new Blob([result]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', "error_record_" + row.time + row.time + '.' + "csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); // Clean up
            window.URL.revokeObjectURL(url); // Free memory
        } catch (error) {
            console.error('Failed to fetch error CSV:', error);
        }
    };

    const handleAllNumClick = async (row: RowData) => {
        try {
            const result = await getAllCsvByTime(row.time).unwrap();
            const url = window.URL.createObjectURL(new Blob([result]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', "all_record_" + row.time + row.time + '.' + "csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); // Clean up
            window.URL.revokeObjectURL(url); // Free memory
        } catch (error) {
            console.error('Failed to fetch all CSV:', error);
        }
    };

    const handleOpen = async (row: RowData) => {
        try {
            const response = await getHistoryByTime(row.time).unwrap();
            setDetails(response);
            setSelectedRow(row);
            setOpen(true);
        } catch (error) {
            console.error('Failed to fetch history by time:', error);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedRow(null);
    };

    // Add manual refresh button
    const handleRefresh = () => {
        refetch();
    };

    return (
        <Layout title="ポイント付与履歴">
            <ToolBar title="ポイント付与履歴" noSearchPanel={true} noAddButton={true} noFilterPanel={true} />
            <br />
            <Paper>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px' }}>
                    <Typography variant="h6" component="div">
                        ポイント付与履歴
                    </Typography>
                    <Button 
                        onClick={handleRefresh} 
                        variant="outlined" 
                        disabled={isLoading}
                    >
                        {isLoading ? '読み込み中...' : '更新'}
                    </Button>
                </div>
                <br/>
                <br/>
                <br/>
                <br/>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>データ件数</TableCell>
                            <TableCell>エラー件数</TableCell>
                            <TableCell>ポイント付与実施日</TableCell>
                            <TableCell>ファイル名</TableCell>
                            <TableCell>詳細</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data === null || data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {isLoading ? '読み込み中...' : '履歴がありません'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.id}</TableCell>
                                    <TableCell>
                                        <Button 
                                            onClick={() => handleAllNumClick(row)}
                                            style={{ textTransform: 'none', padding: 0, minWidth: 'auto', textDecoration: 'underline', color: 'blue' }}
                                        >
                                            {row.num}
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        <Button 
                                            onClick={() => handleErrorNumClick(row)}
                                            style={{ textTransform: 'none', padding: 0, minWidth: 'auto', textDecoration: 'underline', color: 'blue' }}
                                        >
                                            {row.failed}
                                        </Button>
                                    </TableCell>
                                    <TableCell>{row.time}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleOpen(row)}
                                        >
                                            詳細
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={style}>
                    <Typography id="modal-title" variant="h6" component="h2">
                        ポイント付与エラー詳細
                    </Typography>
                    {selectedRow && (
                        <div>
                            <Typography variant="body1"><strong>ID:</strong> {selectedRow.id}</Typography>
                            <Typography variant="body1"><strong>データ件数:</strong> {selectedRow.num}</Typography>
                            <Typography variant="body1"><strong>エラー件数:</strong> {selectedRow.failed}</Typography>
                            <Typography variant="body1"><strong>ポイント付与実施日:</strong> {selectedRow.time}</Typography>
                            <Typography variant="body1"><strong>ファイル名:</strong> {selectedRow.name}</Typography>
                        </div>
                    )}
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ユーザID</TableCell>
                                <TableCell>メール</TableCell>
                                <TableCell>ポイント</TableCell>
                                <TableCell>成否</TableCell>
                                <TableCell>失敗原因</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {details.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No data available
                                    </TableCell>
                                </TableRow>
                            ) : (
                                details.map((detail) => (
                                    <TableRow key={detail.user_id}>
                                        <TableCell>{detail.user_id}</TableCell>
                                        <TableCell>{detail.mail}</TableCell>
                                        <TableCell>{detail.point}</TableCell>
                                        <TableCell>
                                            <span style={{ color: detail.success == 1 ? 'inherit' : 'red' }}>
                                                {detail.success == 1 ? "成功" : "失敗"}
                                            </span>
                                        </TableCell>
                                        <TableCell>{detail.reason}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Modal>
        </Layout>
    );
}

export default ImportHistoryPage;